<script setup lang="ts">
import { NSpin } from "naive-ui";
import { computed, onMounted, reactive } from "vue";
import { requestFile } from "../services/cms";
import { mediaExtensions } from "../utils/constants";
import { downloadBlob, getFileSizeString } from "../utils/file";
import MediaPreview from "./MediaPreview.vue";

const props = defineProps<{
    id: string;
    url: string;
    name: string;
    size?: number;
    hideDelete?: boolean;
}>();

/* eslint-disable no-unused-vars */
const emit = defineEmits<{
    (event: "delete-attachment", fileInfo: { id: string }): void;
}>();
/* eslint-enable no-unused-vars */

const state = reactive({
    imageLoaded: false,
    imageError: false,
    imageDataUrl: null as string | null,
    isPreviewModalOpen: false,
    fileSize: null as number | null,
});

const isMedia = computed(() => {
    const extension = props.name.split(".").pop()?.toLowerCase();
    return extension && mediaExtensions.includes(extension);
});

const isPdf = computed(() => {
    const extension = props.name.split(".").pop()?.toLowerCase();
    return extension === "pdf";
});

const displaySize = computed(() => {
    const size = props.size || state.fileSize;
    return size ? getFileSizeString(size) : null;
});

async function loadFileInfo() {
    // Load file size for all files if not provided via props
    if (!props.size) {
        try {
            const blob = await requestFile(props.url);
            state.fileSize = blob.size;
        } catch (error) {
            console.error("Error loading file info:", error);
        }
    }
}

async function loadImage() {
    if (isMedia.value && !isPdf.value) {
        try {
            const blob = await requestFile(props.url);
            state.fileSize = blob.size;
            const reader = new FileReader();
            reader.onloadend = () => {
                state.imageDataUrl = reader.result as string;
                state.imageLoaded = true;
            };
            reader.onerror = () => {
                console.error("FileReader error:", reader.error);
                state.imageError = true;
            };
            reader.readAsDataURL(blob);
        } catch (error) {
            console.error("Error loading image:", error);
            state.imageError = true;
        }
    } else {
        // For non-media files (PDFs, documents), just load file size
        await loadFileInfo();
    }
}

const onDelete = () => {
    emit("delete-attachment", { id: props.id });
};

const closePreviewModal = () => {
    state.isPreviewModalOpen = false;
};

const onClick = async () => {
    if (isMedia.value) {
        state.isPreviewModalOpen = true;
    } else {
        const blob = await requestFile(props.url);
        // Store file size if we don't have it yet
        if (!state.fileSize && !props.size) {
            state.fileSize = blob.size;
        }
        downloadBlob(props.name, blob);
    }
};

onMounted(() => {
    loadImage();
});
</script>

<template>
    <div class="flex items-center justify-between gap-2">
        <div
            v-if="isPdf"
            class="flex bg-cream rounded justify-between items-center py-1 px-2 w-full cursor-pointer"
            :title="name"
            @click="onClick"
        >
            <span class="text-xs whitespace-nowrap overflow-hidden text-ellipsis">
                {{ name }}
                <span v-if="displaySize" class="text-gray-500">({{ displaySize }})</span>
            </span>
        </div>
        <div
            v-else-if="isMedia && !isPdf && !state.imageError"
            class="w-16 h-16 mr-2 rounded overflow-hidden"
        >
            <div
                v-if="state.imageLoaded && state.imageDataUrl"
                class="cursor-pointer"
                @click="onClick"
            >
                <img :src="state.imageDataUrl" :alt="name" class="w-full h-full object-cover" />
            </div>
            <n-spin v-if="!state.imageLoaded" size="small" />
        </div>
        <div
            v-else
            class="flex bg-cream rounded justify-between items-center py-1 px-2 w-full cursor-pointer"
            title="Click to download"
            @click="onClick"
        >
            <a class="flex items-center cursor-pointer">
                <div class="flex items-center cursor-pointer">
                    {{ name }}
                    <span v-if="displaySize" class="text-gray-500 ml-1">({{ displaySize }})</span>
                </div>
            </a>
        </div>
        <button
            class="material-symbols-outlined text-sm h-5 w-5 rounded-full items-center justify-center hover:bg-dark-mode-red cursor-pointer"
            :class="{ hidden: hideDelete }"
            :title="`Delete ${name}`"
            @click.prevent.stop="onDelete"
        >
            close
        </button>
        <media-preview
            :is-open="state.isPreviewModalOpen"
            :url="url"
            :name="name"
            @close="closePreviewModal"
        />
    </div>
</template>
